/**
 * /affiliate — Affiliate partner landing + dashboard.
 *
 * Two modes:
 *   1. No `?code=…` in URL → signup form pitching the 30% lifetime deal
 *   2. `?code=XYZ` in URL → partner dashboard with stats + shareable link
 *
 * Standalone from /referral (which is end-user social-share rewards).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, Copy, Check, DollarSign, TrendingUp, Users,
  Loader2, Share2, Sparkles, ShieldCheck, Wand2,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { API } from "../config";
import AffiliateLeaderboard from "../components/affiliate/AffiliateLeaderboard";

const COPY = {
  sk: {
    eyebrow: "PARTNERSKÝ PROGRAM",
    h1: "Zarábaj 30 % naživo za každého klienta. Navždy.",
    sub: "Stačí poslať svojím followerom svoj odkaz. Keď si kúpia akýkoľvek plán, dostávaš 30 % provízie z každej platby — donekonečna.",
    bullets: [
      "30% lifetime — nielen prvý mesiac",
      "Cookie 90 dní, atribúcia bez podvodov",
      "Výplata cez PayPal / banku / krypto",
      "Hotový marketing kit (bannery, copy, videá)",
      "Žiadny limit na zárobok",
    ],
    cta: "Stať sa partnerom",
    busy: "Posielam…",
    success_title: "Tvoj partnerský odkaz",
    name_label: "Tvoje meno",
    email_label: "Email pre výplatu",
    payout_label: "Spôsob výplaty",
    audience_label: "Aký máš dosah? (voliteľné)",
    audience_ph: "napr. 12k Instagram, 3k newsletter",
    payout_paypal: "PayPal", payout_bank: "Bankový prevod", payout_crypto: "Krypto",
    payout_details_label: "Údaje pre výplatu",
    notes_label: "Niečo navyše (voliteľné)",
    has_code: "Už mám kód",
    has_code_ph: "Tvoj kód",
    has_code_btn: "Otvoriť dashboard",
    // Dashboard
    dash_share: "Tvoj odkaz",
    dash_share_classic: "Klasický odkaz (alternatíva)",
    dash_copy: "Kopírovať", dash_copied: "Skopírované!",
    settings_h: "Uvítací banner pre návštevníkov",
    settings_sub: "Návštevníci z tvojho odkazu uvidia personalizovaný banner s tvojim menom a zľavou. Zvyšuje konverziu o 15-25%.",
    settings_enabled: "Banner zapnutý",
    settings_code_label: "Zľavový kód",
    settings_percent_label: "Zľava (%)",
    settings_greeting_label: "Vlastný pozdrav (voliteľné)",
    settings_greeting_ph: "napr. \"Vitaj, kamoš Janka\"",
    settings_preview_h: "Náhľad bannera",
    settings_save: "Uložiť",
    settings_saved: "Uložené!",
    kit_h: "Marketing kit",
    kit_sub: "Bannery, copy texty, video script — všetko s tvojím kódom.",
    kit_btn: "Otvoriť kit",
    vanity_label: "Vlastný kód (voliteľné)",
    vanity_ph: "napr. janko",
    vanity_hint: "Krátky čitateľný odkaz, napr. toprankpulse.com/r/janko",
    vanity_taken: "Tento kód je obsadený — pridelili sme náhodný",
    vanity_invalid: "Použi 3–24 znakov: a–z, 0–9, -, _",
    vanity_avail: "✓ Voľný",
    dash_clicks: "Kliknutí", dash_conv: "Konverzií",
    dash_earned: "Celkom zarobené", dash_pending: "Čaká na výplatu",
    dash_recent: "Posledné konverzie",
    dash_empty: "Zatiaľ žiadne konverzie. Pošli odkaz prvým ľuďom.",
    dash_status_pending: "čaká", dash_status_paid: "vyplatené",
    top_creatives_h: "Tvoje top kreatívy",
    top_creatives_sub: "Reálne čísla: ktoré kreatívy ti partneri klikajú a zdieľajú najviac. Tie čo zarobili najviac € sú hore.",
    top_creatives_empty: "Stiahni si pár kreatív v Marketing balíčku, potom uvidíš, ktoré ti najviac fungujú.",
    tc_downloads: "stiah.", tc_copies: "kópie", tc_shares: "zdieľ.",
    tc_winner: "Najlepší",
    tc_conv: "konverzií",
    tc_earned_total: "Zarobené cez kreatívy",
    tc_efficient: "Top efektivita",
    tc_efficient_tip: "Najviac € na 1 share — najefektívnejšia kreatíva",
    tc_share_unit: "share",
    cap_h: "AI captions — výkonnosť",
    cap_sub: "Posledný-touch attribution v 72h okne: koľko € ti zarobili AI captions vs ostatný flow.",
    cap_ai: "AI captions", cap_other: "Default / direct",
    cap_share_pct: "podiel AI",
    cap_generated: "vygenerovaných", cap_used: "použitých",
    cap_empty: "Zatiaľ žiadne použitia. Skús AI Copy Coach v Marketing balíčku.",
    cap_top: "Top zarábajúce captions",
    cap_no_earned: "Captions už používaš, ale ešte sa nepripísala konverzia. Daj im chvíľu.",
    audience_type_label: "Aký máš typ publika?",
    audience_type_b2b: "B2B / SaaS",
    audience_type_ecommerce: "E-commerce",
    audience_type_creators: "Tvorcovia / influenceri",
    audience_type_agency: "Agentúra / freelancer",
    audience_type_other: "Iné",
    suggest_h: "Tipy pre teba",
    suggest_sub_segment: "Tieto kreatívy najviac fungujú partnerom s podobným publikom ({segment}).",
    suggest_sub_global: "Najpopulárnejšie kreatívy naprieč celou sieťou. Pridaj svoj typ publika v dashboarde a získaš lepšie tipy.",
    suggest_sub_cold: "Najnovšie kreatívy. Ako sa siet rozrastá, uvidíš čo funguje partnerom s rovnakým publikom.",
    suggest_winner: "Top voľba",
    suggest_used: "Už si stiahol",
    suggest_partners: "{n} partnerov",
    leaderboard: {
      title: "Top zarábajúci partneri — naživo",
      subtitle: "30% lifetime · aktualizované automaticky",
      stat_partners: "Aktívni partneri",
      stat_paid: "Vyplatené partnerom",
      stat_conv: "Sledované predaje",
      sale: "predaj",
      sales: "predajov",
      cta_strip: "Pridaj sa k stovkám partnerov, ktorí zarábajú 30 % naživo za každého klienta.",
    },
  },
  en: {
    eyebrow: "PARTNER PROGRAM",
    h1: "Earn 30% lifetime on every customer. Forever.",
    sub: "Just share your link with your audience. When they buy any plan, you earn 30% on every single payment — forever.",
    bullets: [
      "30% lifetime — not just first month",
      "90-day cookie, fraud-proof attribution",
      "Payouts via PayPal / bank / crypto",
      "Ready marketing kit (banners, copy, videos)",
      "No earnings cap",
    ],
    cta: "Become a partner",
    busy: "Submitting…",
    success_title: "Your partner link",
    name_label: "Your name",
    email_label: "Payout email",
    payout_label: "Payout method",
    audience_label: "Audience size? (optional)",
    audience_ph: "e.g. 12k Instagram, 3k newsletter",
    payout_paypal: "PayPal", payout_bank: "Bank transfer", payout_crypto: "Crypto",
    payout_details_label: "Payout details",
    notes_label: "Anything else? (optional)",
    has_code: "Have a code?",
    has_code_ph: "Your code",
    has_code_btn: "Open dashboard",
    dash_share: "Your link",
    dash_share_classic: "Classic link (alternative)",
    dash_copy: "Copy", dash_copied: "Copied!",
    settings_h: "Welcome banner for visitors",
    settings_sub: "Visitors from your link see a personalised banner with your name and a discount. Boosts conversion 15–25%.",
    settings_enabled: "Banner enabled",
    settings_code_label: "Discount code",
    settings_percent_label: "Discount (%)",
    settings_greeting_label: "Custom greeting (optional)",
    settings_greeting_ph: "e.g. \"Welcome from Jane's audience\"",
    settings_preview_h: "Banner preview",
    settings_save: "Save",
    settings_saved: "Saved!",
    kit_h: "Marketing kit",
    kit_sub: "Banners, copy templates, video script — all baked with your code.",
    kit_btn: "Open kit",
    vanity_label: "Custom code (optional)",
    vanity_ph: "e.g. johndoe",
    vanity_hint: "Short readable link like toprankpulse.com/r/johndoe",
    vanity_taken: "That code is taken — we assigned a random one",
    vanity_invalid: "Use 3–24 chars: a–z, 0–9, -, _",
    vanity_avail: "✓ Available",
    dash_clicks: "Clicks", dash_conv: "Conversions",
    dash_earned: "Total earned", dash_pending: "Pending payout",
    dash_recent: "Recent conversions",
    dash_empty: "No conversions yet. Share your link with the first people.",
    dash_status_pending: "pending", dash_status_paid: "paid",
    top_creatives_h: "Your top creatives",
    top_creatives_sub: "Real data: which creatives your audience clicks and shares the most. Ones that earned the most € are on top.",
    top_creatives_empty: "Download a few creatives from the Marketing Pack — then you'll see which ones perform best for you.",
    tc_downloads: "downloads", tc_copies: "copies", tc_shares: "shares",
    tc_winner: "Winner",
    tc_conv: "conversions",
    tc_earned_total: "Earned via creatives",
    tc_efficient: "Top efficiency",
    tc_efficient_tip: "Highest € per share — most efficient creative",
    tc_share_unit: "share",
    cap_h: "AI captions — performance",
    cap_sub: "Last-touch attribution in a 72-h window: how much € your AI captions earned vs the rest of the flow.",
    cap_ai: "AI captions", cap_other: "Default / direct",
    cap_share_pct: "AI share",
    cap_generated: "generated", cap_used: "used",
    cap_empty: "No caption uses yet. Try the AI Copy Coach in the Marketing Pack.",
    cap_top: "Top earning captions",
    cap_no_earned: "Captions used, but no conversion attributed yet. Give them a bit of time.",
    audience_type_label: "What kind of audience do you have?",
    audience_type_b2b: "B2B / SaaS",
    audience_type_ecommerce: "E-commerce",
    audience_type_creators: "Creators / influencers",
    audience_type_agency: "Agency / freelancer",
    audience_type_other: "Other",
    suggest_h: "Picked for you",
    suggest_sub_segment: "These creatives perform best for partners with a similar audience ({segment}).",
    suggest_sub_global: "Top-performing creatives across the whole network. Set your audience type in the dashboard for better picks.",
    suggest_sub_cold: "Latest creatives. As the network grows, we'll show what works for partners like you.",
    suggest_winner: "Top pick",
    suggest_used: "Already used",
    suggest_partners: "{n} partners",
    leaderboard: {
      title: "Top Earners — Live",
      subtitle: "30% lifetime · auto-updated",
      stat_partners: "Active partners",
      stat_paid: "Paid to partners",
      stat_conv: "Tracked sales",
      sale: "sale",
      sales: "sales",
      cta_strip: "Join hundreds of partners earning 30% lifetime on every referral.",
    },
  },
};

export default function AffiliatePage() {
  const { language } = useLanguage();
  const [params, setParams] = useSearchParams();
  const t = COPY[language] || COPY.en;
  const code = params.get("code") || "";

  if (code) {
    return <AffiliateDashboard code={code} t={t} />;
  }
  return <AffiliateSignup t={t} onSignedUp={(c) => setParams({ code: c })} />;
}

function AffiliateSignup({ t, onSignedUp }) {
  const [form, setForm] = useState({
    name: "", email: "", payout_method: "paypal",
    payout_details: "", audience_size: "", audience_type: "other",
    notes: "", desired_code: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [existingCode, setExistingCode] = useState("");
  const [vanityState, setVanityState] = useState({ checking: false, available: null, reason: null });

  // Debounced inline check for vanity availability — single API call per
  // 400ms of inactivity so admin doesn't get hammered while typing.
  useEffect(() => {
    const slug = form.desired_code.trim().toLowerCase();
    if (!slug) { setVanityState({ checking: false, available: null, reason: null }); return; }
    setVanityState(s => ({ ...s, checking: true }));
    const id = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/affiliate/check-vanity/${encodeURIComponent(slug)}`);
        setVanityState({ checking: false, available: r.data.available, reason: r.data.reason || null });
      } catch {
        setVanityState({ checking: false, available: null, reason: null });
      }
    }, 400);
    return () => clearTimeout(id);
  }, [form.desired_code]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      const r = await axios.post(`${API}/affiliate/signup`, form);
      if (r.data?.vanity_taken) {
        // Surface to the user that their chosen code was unavailable.
        alert(t.vanity_taken);
      }
      onSignedUp(r.data.code);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden" data-testid="affiliate-page">
      <header className="border-b border-zinc-800 py-4 px-6">
        <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-16">
        <p className="text-xs font-mono tracking-[0.2em] sm:tracking-[0.3em] text-[#FFE600] mb-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#FFE600]/30 rounded-full">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />{t.eyebrow}
        </p>
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] mb-5 leading-tight break-words">{t.h1}</h1>
        <p className="text-base sm:text-lg text-zinc-400 mb-10 max-w-3xl">{t.sub}</p>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Bullets */}
          <div className="lg:col-span-2 min-w-0">
            <ul className="space-y-3 mb-8" data-testid="affiliate-bullets">
              {t.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <ShieldCheck className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                  <span className="min-w-0 break-words">{b}</span>
                </li>
              ))}
            </ul>
            <div className="bg-[#141414] border-2 border-zinc-800 p-5">
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">{t.has_code}</p>
              <div className="flex gap-2">
                <input
                  type="text" value={existingCode}
                  onChange={(e) => setExistingCode(e.target.value)}
                  placeholder={t.has_code_ph}
                  className="flex-1 px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-sm font-mono"
                  data-testid="affiliate-existing-code-input"
                />
                <Link
                  to={`/affiliate?code=${encodeURIComponent(existingCode.trim().toLowerCase())}`}
                  className="px-4 py-2 bg-zinc-800 text-xs font-bold hover:bg-zinc-700 uppercase tracking-wider inline-flex items-center gap-1"
                  data-testid="affiliate-existing-code-go"
                >
                  {t.has_code_btn} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>

          {/* Signup form */}
          <form onSubmit={submit} className="lg:col-span-3 bg-[#141414] border-2 border-zinc-800 p-6 sm:p-8 space-y-4"
                data-testid="affiliate-signup-form">
            <Field label={t.name_label}>
              <input required minLength={1} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                data-testid="affiliate-input-name" />
            </Field>
            <Field label={t.email_label}>
              <input required type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                data-testid="affiliate-input-email" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t.payout_label}>
                <select value={form.payout_method}
                  onChange={(e) => setForm({ ...form, payout_method: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                  data-testid="affiliate-input-payout-method">
                  <option value="paypal">{t.payout_paypal}</option>
                  <option value="bank">{t.payout_bank}</option>
                  <option value="crypto">{t.payout_crypto}</option>
                </select>
              </Field>
              <Field label={t.audience_label}>
                <input value={form.audience_size}
                  onChange={(e) => setForm({ ...form, audience_size: e.target.value })}
                  placeholder={t.audience_ph}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                  data-testid="affiliate-input-audience" />
              </Field>
            </div>
            <Field label={t.audience_type_label}>
              <select value={form.audience_type}
                onChange={(e) => setForm({ ...form, audience_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                data-testid="affiliate-input-audience-type">
                <option value="b2b">{t.audience_type_b2b}</option>
                <option value="ecommerce">{t.audience_type_ecommerce}</option>
                <option value="creators">{t.audience_type_creators}</option>
                <option value="agency">{t.audience_type_agency}</option>
                <option value="other">{t.audience_type_other}</option>
              </select>
            </Field>
            <Field label={t.payout_details_label}>
              <input value={form.payout_details}
                onChange={(e) => setForm({ ...form, payout_details: e.target.value })}
                placeholder={form.payout_method === "paypal" ? "paypal@email.com" : form.payout_method === "bank" ? "IBAN" : "Wallet address"}
                className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                data-testid="affiliate-input-payout-details" />
            </Field>
            <Field label={t.notes_label}>
              <textarea rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
                data-testid="affiliate-input-notes" />
            </Field>
            <Field label={t.vanity_label}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-mono pointer-events-none">/r/</span>
                <input value={form.desired_code}
                  onChange={(e) => setForm({ ...form, desired_code: e.target.value.toLowerCase() })}
                  placeholder={t.vanity_ph}
                  maxLength={24}
                  className="w-full pl-10 pr-24 py-3 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none font-mono text-sm"
                  data-testid="affiliate-input-vanity" />
                {form.desired_code && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold" data-testid="affiliate-vanity-status">
                    {vanityState.checking
                      ? <span className="text-zinc-500">…</span>
                      : vanityState.available === true
                        ? <span className="text-[#10B981]">{t.vanity_avail}</span>
                        : vanityState.available === false
                          ? <span className="text-red-400">{vanityState.reason === "invalid" ? t.vanity_invalid : "× taken"}</span>
                          : null}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">{t.vanity_hint}</p>
            </Field>
            {err && <p className="text-xs text-red-400">{err}</p>}
            <button type="submit" disabled={submitting}
              className="w-full px-6 py-4 bg-[#FFE600] text-black font-black uppercase tracking-wider transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="affiliate-submit">
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />{t.busy}</> : <>{t.cta} <ArrowRight className="w-5 h-5" /></>}
            </button>
          </form>
        </div>
      </section>

      {/* Top Earners leaderboard — social proof for prospective partners */}
      <AffiliateLeaderboard limit={10} t={t.leaderboard || {}} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function AffiliateDashboard({ code, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/affiliate/me`, { params: { code } });
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [code]);

  const [competitors, setCompetitors] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/comparison/competitors`)
      .then(r => { if (!cancelled) setCompetitors(r.data?.competitors || []); })
      .catch(() => { /* optional section */ });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">Code not found.</p>
          <Link to="/affiliate" className="text-[#FFE600] underline">← Back</Link>
        </div>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="affiliate-dashboard">
      <header className="border-b border-zinc-800 py-4 px-6">
        <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-12">
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">TopRankPulse · {data.code}</p>
          <h1 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk']">{data.name}</h1>
        </div>

        {/* Share links — short + classic */}
        <div className="bg-[#141414] border-2 border-[#FFE600]/40 p-5 mb-8 space-y-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFE600] mb-2 flex items-center gap-1">
              <Share2 className="w-3 h-3" />{t.dash_share}
            </p>
            <div className="flex gap-2 flex-col sm:flex-row">
              <input readOnly value={data.share_url}
                className="flex-1 px-4 py-3 bg-[#0a0a0a] border border-zinc-700 font-mono text-sm"
                data-testid="affiliate-share-url" />
              <button onClick={copy}
                className="px-4 py-3 bg-[#FFE600] text-black font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2"
                data-testid="affiliate-copy-btn">
                {copied ? <><Check className="w-4 h-4" />{t.dash_copied}</> : <><Copy className="w-4 h-4" />{t.dash_copy}</>}
              </button>
            </div>
          </div>
          {data.share_urls?.classic && data.share_urls.classic !== data.share_url && (
            <details className="text-xs text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300">{t.dash_share_classic}</summary>
              <input readOnly value={data.share_urls.classic}
                className="w-full mt-2 px-3 py-2 bg-[#0a0a0a] border border-zinc-800 font-mono text-xs"
                data-testid="affiliate-share-url-classic" />
            </details>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Users}      label={t.dash_clicks}  value={data.total_clicks} testid="affiliate-stat-clicks" />
          <Stat icon={TrendingUp} label={t.dash_conv}    value={data.total_conversions} testid="affiliate-stat-conversions" />
          <Stat icon={DollarSign} label={t.dash_earned}  value={`€${data.total_earned_eur.toFixed(2)}`} accent="#FFE600" testid="affiliate-stat-earned" />
          <Stat icon={DollarSign} label={t.dash_pending} value={`€${data.pending_eur.toFixed(2)}`} accent="#10B981" testid="affiliate-stat-pending" />
        </div>

        {/* Top share pages — sends partner to comparison landings with their code */}
        {competitors.length > 0 && (
          <div className="bg-[#141414] border-2 border-zinc-800 mb-8 p-5" data-testid="affiliate-share-pages">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFE600] flex items-center gap-1">
                <Share2 className="w-3 h-3" />Top share pages
              </p>
              <Link to={`/affiliate-kit?code=${data.code}`}
                className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded inline-flex items-center gap-1.5 font-bold uppercase tracking-wider"
                data-testid="affiliate-kit-link">
                {t.kit_btn} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Tieto stránky konvertujú 3–5× lepšie ako holá landing. Zdieľaj ich svojím followerom:
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {competitors.map((c) => (
                <a key={c.slug}
                  href={`/vs/${c.slug}?ref=${data.code}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-between gap-3 px-3 py-2.5 bg-[#0a0a0a] border border-zinc-800 hover:border-zinc-500 transition-colors group"
                  data-testid={`affiliate-share-page-${c.slug}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color }} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">TopRankPulse vs {c.name}</p>
                      <p className="text-[10px] text-zinc-500 font-mono truncate">/vs/{c.slug}?ref={data.code}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-white flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Welcome banner settings */}
        <WelcomeSettings data={data} t={t} onSaved={(patch) => setData(d => ({ ...d, ...patch }))} />

        {/* Audience type — drives Smart Suggestions in /affiliate-kit */}
        <AudienceTypeEditor data={data} t={t} onSaved={(patch) => setData(d => ({ ...d, ...patch }))} />

        {/* Top creatives — performance ranking of marketing-pack assets */}
        <TopCreatives code={data.code} t={t} />

        {/* AI captions — last-touch attribution dashboard */}
        <CaptionPerformance code={data.code} t={t} />

        {/* Recent conversions table */}
        <div className="bg-[#141414] border-2 border-zinc-800">
          <div className="px-5 py-3 border-b border-zinc-800">
            <p className="text-xs font-mono uppercase tracking-wider text-zinc-400">{t.dash_recent}</p>
          </div>
          {data.recent_conversions.length === 0 ? (
            <p className="p-10 text-center text-sm text-zinc-500" data-testid="affiliate-empty">{t.dash_empty}</p>
          ) : (
            <table className="w-full text-sm" data-testid="affiliate-conversions-table">
              <thead className="bg-[#0a0a0a]/40 text-zinc-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                  <th className="text-right px-4 py-2.5">Commission</th>
                  <th className="text-center px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_conversions.map((c, i) => (
                  <tr key={i} className="border-t border-zinc-800/60">
                    <td className="px-4 py-3 font-mono text-zinc-300">{c.customer_email}</td>
                    <td className="px-4 py-3 text-right text-zinc-400">€{(c.amount_eur || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#FFE600]">€{(c.commission_eur || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${c.status === "paid" ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#FFE600]/20 text-[#FFE600]"}`}>
                        {c.status === "paid" ? t.dash_status_paid : t.dash_status_pending}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-zinc-500">
                      {(c.created_at || "").slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "#fff", testid }) {
  return (
    <div className="bg-[#141414] border-2 border-zinc-800 p-5" data-testid={testid}>
      <Icon className="w-4 h-4 mb-3" style={{ color: accent }} />
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
      <p className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk']" style={{ color: accent }}>{value}</p>
    </div>
  );
}


function WelcomeSettings({ data, t, onSaved }) {
  const [enabled, setEnabled] = useState(!!data.welcome_banner_enabled);
  const [code, setCode] = useState(data.welcome_discount_code || "");
  const [percent, setPercent] = useState(data.welcome_discount_percent ?? 10);
  const [greeting, setGreeting] = useState(data.custom_greeting || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/affiliate/settings`, {
        code: data.code,
        welcome_banner_enabled: enabled,
        welcome_discount_code: code,
        welcome_discount_percent: percent,
        custom_greeting: greeting,
      });
      onSaved({
        welcome_banner_enabled: enabled,
        welcome_discount_code: code || `${data.code.toUpperCase()}10`,
        welcome_discount_percent: percent,
        custom_greeting: greeting || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const greetingPreview = greeting || `${data.name} ťa odporučil`;
  const codePreview = (code || `${data.code.toUpperCase()}10`).toUpperCase();

  return (
    <div className="bg-[#141414] border-2 border-zinc-800 mb-8 p-5" data-testid="affiliate-welcome-settings">
      <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFE600] mb-3 flex items-center gap-1">
        <Sparkles className="w-3 h-3" />{t.settings_h}
      </p>
      <p className="text-xs text-zinc-500 mb-5">{t.settings_sub}</p>

      <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
        <input
          type="checkbox" checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-4 h-4 accent-[#FFE600]"
          data-testid="welcome-enabled-toggle"
        />
        <span className="text-sm font-bold">{t.settings_enabled}</span>
      </label>

      <div className={`grid sm:grid-cols-2 gap-3 mb-4 transition-opacity ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <Field label={t.settings_code_label}>
          <input value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={`${data.code.toUpperCase()}10`}
            maxLength={20}
            className="w-full px-3 py-2 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none font-mono uppercase"
            data-testid="welcome-discount-code" />
        </Field>
        <Field label={t.settings_percent_label}>
          <input type="number" min={0} max={50}
            value={percent}
            onChange={(e) => setPercent(Math.max(0, Math.min(50, parseInt(e.target.value || "0", 10))))}
            className="w-full px-3 py-2 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none font-mono"
            data-testid="welcome-discount-percent" />
        </Field>
      </div>
      <div className={`mb-4 transition-opacity ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <Field label={t.settings_greeting_label}>
          <input value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
            placeholder={t.settings_greeting_ph}
            maxLength={200}
            className="w-full px-3 py-2 bg-[#0a0a0a] border-2 border-zinc-700 focus:border-[#FFE600] focus:outline-none"
            data-testid="welcome-custom-greeting" />
        </Field>
      </div>

      {/* Live preview */}
      {enabled && (
        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">{t.settings_preview_h}</p>
          <div className="bg-gradient-to-r from-[#FFE600] to-yellow-300 text-black px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="welcome-preview">
            <Sparkles className="w-4 h-4" />
            <span className="font-bold">{greetingPreview}.</span>
            <span className="hidden sm:inline">Získaj {percent}% zľavu kódom</span>
            <code className="px-2 py-0.5 bg-black text-[#FFE600] font-black rounded text-xs">{codePreview}</code>
          </div>
        </div>
      )}

      <button type="button" onClick={save} disabled={saving}
        className="px-5 py-2 bg-[#FFE600] text-black font-bold uppercase tracking-wider text-sm hover:bg-yellow-300 disabled:opacity-60 inline-flex items-center gap-2"
        data-testid="welcome-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (saved ? <Check className="w-4 h-4" /> : null)}
        {saved ? t.settings_saved : t.settings_save}
      </button>
    </div>
  );
}

function TopCreatives({ code, t }) {
  const [state, setState] = useState({ loading: true, ranked: [], totals: {} });
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    axios.get(`${API}/affiliate/top-creatives/${encodeURIComponent(code)}?limit=5`)
      .then(r => { if (!cancelled) setState({ loading: false, ...r.data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, ranked: [], totals: {} }); });
    return () => { cancelled = true; };
  }, [code]);

  if (state.loading) return null;
  const isEmpty = !state.ranked || state.ranked.length === 0;

  return (
    <div className="bg-[#141414] border-2 border-zinc-800 mb-8 p-5" data-testid="affiliate-top-creatives">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFE600] flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />{t.top_creatives_h}
        </p>
        <Link to={`/affiliate-kit?code=${code}`}
          className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded inline-flex items-center gap-1.5 font-bold uppercase tracking-wider"
          data-testid="affiliate-top-creatives-kit-link">
          {t.kit_btn} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <p className="text-xs text-zinc-500 mb-4">{t.top_creatives_sub}</p>

      {isEmpty ? (
        <p className="text-sm text-zinc-500 italic" data-testid="affiliate-top-creatives-empty">
          {t.top_creatives_empty}
        </p>
      ) : (
        <>
          {state.earned_total_eur > 0 && (
            <p className="text-xs text-emerald-400 font-mono mb-3" data-testid="affiliate-top-creatives-earned">
              {t.tc_earned_total}: €{state.earned_total_eur.toFixed(2)}
            </p>
          )}
          <div className="space-y-2">
            {state.ranked.map((r, i) => (
              <div key={r.id}
                className="flex items-center gap-3 px-3 py-2.5 bg-[#0a0a0a] border border-zinc-800"
                data-testid={`affiliate-top-creative-${r.id}`}>
                <div className="w-10 h-10 flex-shrink-0 bg-black border border-zinc-800 overflow-hidden">
                  <img src={r.preview_url} alt={r.label} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-white truncate">{r.label}</p>
                    {i === 0 && (
                      <span className="px-1.5 py-0.5 bg-[#FFE600] text-black text-[9px] font-black uppercase tracking-wider">
                        {t.tc_winner}
                      </span>
                    )}
                    {r.is_most_efficient && (
                      <span className="px-1.5 py-0.5 bg-violet-500 text-white text-[9px] font-black uppercase tracking-wider"
                        data-testid={`affiliate-top-creative-${r.id}-efficient`}
                        title={t.tc_efficient_tip}>
                        {t.tc_efficient}
                      </span>
                    )}
                    {r.conv_eur > 0 && (
                      <span className="px-1.5 py-0.5 bg-emerald-500 text-black text-[9px] font-black uppercase tracking-wider"
                        data-testid={`affiliate-top-creative-${r.id}-earned`}
                        title={`${r.conv_count} ${t.tc_conv}`}>
                        €{r.conv_eur.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-500 font-mono">
                    {r.shares} {t.tc_shares} · {r.downloads} {t.tc_downloads} · {r.copies} {t.tc_copies}
                    {r.eur_per_share > 0 && (
                      <span className="ml-2 text-violet-400" data-testid={`affiliate-top-creative-${r.id}-eps`}>
                        · €{r.eur_per_share.toFixed(2)}/{t.tc_share_unit}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function AudienceTypeEditor({ data, t, onSaved }) {
  const [type, setType] = useState(data?.audience_type || "other");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const options = [
    { v: "b2b",        label: t.audience_type_b2b },
    { v: "ecommerce",  label: t.audience_type_ecommerce },
    { v: "creators",   label: t.audience_type_creators },
    { v: "agency",     label: t.audience_type_agency },
    { v: "other",      label: t.audience_type_other },
  ];
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/affiliate/audience-type`, { code: data.code, audience_type: type });
      if (r.data?.ok) {
        onSaved({ audience_type: r.data.audience_type });
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      }
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-[#141414] border-2 border-zinc-800 mb-8 p-5" data-testid="audience-type-editor">
      <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFE600] mb-2 flex items-center gap-1">
        <Sparkles className="w-3 h-3" />{t.audience_type_label}
      </p>
      <p className="text-xs text-zinc-500 mb-4">{t.suggest_sub_segment.replace("({segment})", "")}</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {options.map(o => (
          <button key={o.v} type="button"
            onClick={() => setType(o.v)}
            data-testid={`audience-type-${o.v}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${type === o.v ? "bg-[#FFE600] text-black border-[#FFE600]" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <button type="button" onClick={save} disabled={saving || type === (data?.audience_type || "other")}
        className="px-5 py-2 bg-[#FFE600] text-black font-bold uppercase tracking-wider text-sm hover:bg-yellow-300 disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="audience-type-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (saved ? <Check className="w-4 h-4" /> : null)}
        {saved ? t.settings_saved : t.settings_save}
      </button>
    </div>
  );
}

function CaptionPerformance({ code, t }) {
  const [state, setState] = useState({ loading: true, ranked: [], summary: null });
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    axios.get(`${API}/affiliate/caption-performance/${encodeURIComponent(code)}?limit=5`)
      .then(r => { if (!cancelled) setState({ loading: false, ...r.data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, ranked: [], summary: null }); });
    return () => { cancelled = true; };
  }, [code]);

  if (state.loading) return null;
  const s = state.summary;
  // Hide widget entirely until the partner has at least generated something.
  if (!s || (s.captions_generated || 0) === 0) return null;

  const aiPct = s.ai_share_pct || 0;
  const otherPct = Math.max(0, 100 - aiPct);
  const earnedAny = (s.total_eur || 0) > 0;
  const haveTopList = Array.isArray(state.ranked) && state.ranked.length > 0;

  return (
    <div className="bg-[#141414] border-2 border-violet-500/30 mb-8 p-5" data-testid="affiliate-caption-performance">
      <p className="text-[10px] font-mono uppercase tracking-wider text-violet-400 flex items-center gap-1 mb-2">
        <Wand2 className="w-3 h-3" />{t.cap_h}
      </p>
      <p className="text-xs text-zinc-500 mb-4">{t.cap_sub}</p>

      {/* Comparison bar — AI vs Other */}
      {earnedAny && (
        <div className="mb-4" data-testid="affiliate-caption-comparison">
          <div className="flex h-2 overflow-hidden mb-2">
            <div className="bg-violet-500" style={{ width: `${aiPct}%` }} />
            <div className="bg-zinc-700" style={{ width: `${otherPct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] font-mono">
            <span className="text-violet-300">{t.cap_ai}: <strong className="text-white">€{(s.ai_eur || 0).toFixed(2)}</strong> ({aiPct}%)</span>
            <span className="text-zinc-400">{t.cap_other}: <strong className="text-white">€{(s.other_eur || 0).toFixed(2)}</strong></span>
          </div>
        </div>
      )}

      <p className="text-[10px] font-mono text-zinc-500 mb-3" data-testid="affiliate-caption-counts">
        {s.captions_generated} {t.cap_generated} · {s.captions_used} {t.cap_used}
      </p>

      {/* Top earning captions */}
      {!haveTopList ? (
        <p className="text-xs text-zinc-500 italic" data-testid="affiliate-caption-empty">
          {s.captions_used > 0 ? t.cap_no_earned : t.cap_empty}
        </p>
      ) : (
        <>
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">
            {t.cap_top}
          </p>
          <div className="space-y-2">
            {state.ranked.map((r) => (
              <div key={r.caption_id}
                className="px-3 py-2 bg-[#0a0a0a] border border-zinc-800"
                data-testid={`affiliate-caption-${r.caption_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase">
                    <span className="text-violet-400">{r.angle}</span>
                    <span className="text-zinc-500">· {r.channel}</span>
                    {r.locale && <span className="text-zinc-500">· {r.locale}</span>}
                  </div>
                  {r.conv_eur > 0 && (
                    <span className="px-1.5 py-0.5 bg-emerald-500 text-black text-[9px] font-black uppercase tracking-wider"
                      data-testid={`affiliate-caption-${r.caption_id}-earned`}>
                      €{r.conv_eur.toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-200 line-clamp-2">{r.snippet}</p>
                <p className="text-[10px] text-zinc-500 font-mono mt-1">
                  {r.shares} {t.tc_shares} · {r.copies} {t.tc_copies}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

